import { CalculationResult, ScenarioInput, SectorId } from '../types';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const round = (value: number, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const sectorMultiplier: Record<SectorId, number> = {
  forestry: 1.0,
  agriculture: 0.88,
  renewables: 0.95,
  efficiency: 0.82,
  waste: 1.15,
  wetlands: 1.08,
};

function sectorGross(input: ScenarioInput): number {
  switch (input.sector) {
    case 'forestry':
      return input.areaHa * (input.biomassIncrement * 1.67 + input.soilSequestration * 0.75);
    case 'agriculture':
      return input.areaHa * (input.soilSequestration + input.yieldDelta * input.residueFactor * 2.1);
    case 'renewables':
      return input.energySavedMWh * input.gridFactor;
    case 'efficiency':
      return input.energySavedMWh * input.gridFactor * input.systemEfficiency;
    case 'waste':
      return input.methaneAbatementTco2e + input.wastewaterLoad * 1200;
    case 'wetlands':
      return input.areaHa * (input.waterIndex * 5.2 + input.soilSequestration * 0.8) * (0.5 + input.wetlandAreaShare);
  }
}

function qualityScore(input: ScenarioInput, geoAreaHa: number, rasterSignal: number): number {
  const completeness =
    [input.projectName, input.region].filter(Boolean).length / 2 +
    (input.areaHa > 0 ? 1 : 0) +
    (input.durationYears > 0 ? 1 : 0);
  const areaAlignment = geoAreaHa > 0 ? clamp(1 - Math.abs(geoAreaHa - input.areaHa) / Math.max(input.areaHa, geoAreaHa), 0, 1) : 0.6;
  const rsBonus = clamp(rasterSignal, 0, 1);
  return round(clamp((completeness / 4) * 35 + areaAlignment * 40 + rsBonus * 25, 0, 100), 0);
}

function riskScore(input: ScenarioInput, geoAreaHa: number, rasterSignal: number): number {
  const sizeRisk = input.areaHa < 50 ? 18 : input.areaHa < 500 ? 10 : 4;
  const alignmentRisk = geoAreaHa > 0 ? clamp(Math.abs(geoAreaHa - input.areaHa) / Math.max(geoAreaHa, input.areaHa) * 40, 0, 30) : 12;
  const rasterRisk = clamp((1 - rasterSignal) * 22, 0, 22);
  const permanenceRisk = clamp((1 - input.permanenceRate) * 40, 0, 20);
  return round(clamp(sizeRisk + alignmentRisk + rasterRisk + permanenceRisk, 0, 100), 0);
}

export function calculateScenario(
  input: ScenarioInput,
  geoAreaHa = 0,
  rasterNdvi = 0.5,
): CalculationResult {
  const grossBase = sectorGross(input);
  const rampFactor = input.rampUpYears > 1 ? 0.7 + Math.min(0.3, 0.1 * input.rampUpYears) : 1;
  const permanenceFactor = clamp(input.permanenceRate, 0.5, 0.99);
  const leakageFactor = clamp(input.leakageRate, 0, 0.35);
  const bufferFactor = clamp(input.bufferShare, 0, 0.35);
  const rasterSignal = clamp((rasterNdvi + 1) / 2, 0, 1);
  const grossCredits = round(grossBase * sectorMultiplier[input.sector], 2);
  const leakageCredits = round(grossCredits * leakageFactor, 2);
  const bufferCredits = round(grossCredits * bufferFactor, 2);
  const permanenceCredits = round(grossCredits * (1 - permanenceFactor), 2);
  const netCredits = round(grossCredits - leakageCredits - bufferCredits - permanenceCredits, 2);

  const annualSeries = Array.from({ length: input.durationYears }, (_, index) => {
    const year = input.startYear + index;
    const ramp = Math.min(1, (index + 1) / Math.max(1, input.rampUpYears)) * rampFactor;
    const decay = Math.max(0.7, 1 - index * input.degradationRate);
    const factor = round(ramp * decay, 4);
    const gross = round(grossCredits * factor, 2);
    const net = round(gross * (1 - leakageFactor - bufferFactor) * permanenceFactor, 2);
    const revenue = round(net * input.creditPrice, 2);
    const cashFlow = round(revenue - input.opexAnnual, 2);
    const discountedCashFlow = round(cashFlow / Math.pow(1 + input.discountRate, index + 1), 2);
    return { year, gross, net, revenue, cashFlow, discountedCashFlow, factor };
  });

  const grossRevenue = round(annualSeries.reduce((sum, row) => sum + row.revenue, 0), 2);
  const netCashFlow = round(annualSeries.reduce((sum, row) => sum + row.cashFlow, 0) - input.capitalCost, 2);
  const npv = round(annualSeries.reduce((sum, row) => sum + row.discountedCashFlow, 0) - input.capitalCost, 2);
  let cumulative = -input.capitalCost;
  let paybackYears: number | null = null;
  for (const row of annualSeries) {
    cumulative += row.cashFlow;
    if (paybackYears === null && cumulative >= 0) {
      paybackYears = round(row.year - input.startYear + 1, 1);
    }
  }

  const findings: string[] = [];
  findings.push(`方法学倾向: ${input.sector}`);
  if (geoAreaHa > 0) {
    findings.push(`GIS 面积 ${round(geoAreaHa, 1)} ha，与申报面积偏差 ${round(Math.abs(geoAreaHa - input.areaHa), 1)} ha`);
  } else {
    findings.push('尚未上传有效边界，GIS 约束未启用');
  }
  if (rasterNdvi > 0) {
    findings.push(`遥感 NDVI 均值约 ${round(rasterNdvi, 3)}，可作为覆盖度和长势参考`);
  } else {
    findings.push('尚未形成可用遥感样本');
  }
  if (input.sector === 'forestry') {
    findings.push('林业建议额外关注永久性、火险和边界一致性');
  }
  if (input.sector === 'wetlands') {
    findings.push('湿地建议叠加潮位、水文季节和淹水频率校验');
  }

  return {
    grossCredits,
    leakageCredits,
    bufferCredits,
    permanenceCredits,
    netCredits,
    grossRevenue,
    netCashFlow,
    npv,
    paybackYears,
    qualityScore: qualityScore(input, geoAreaHa, rasterSignal),
    riskScore: riskScore(input, geoAreaHa, rasterSignal),
    annualSeries,
    findings,
  };
}
